import { useCallback, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { always, cond, equals, isEmpty } from 'ramda';
import { useSelector } from 'react-redux';

import AccessControl from '@gef-ui/cdu-user/components/AccessControl';
import AngleDown from '@gef-ui/components/atoms/SvgIcon/Icons/AngleDown';
import { getLabelMessage } from '@gef-ui/localization/messages';
import MenuPopover from '@gef-ui/material/components/MenuPopover';
import { getRestUrl } from '@gef-ui/middleware-api/facade';

import { TEMP_CAMPAIGN_DISABLE } from 'features/common/acl/constants';
import { getCurrentRole, getRoles } from 'features/views/user/selectors';

const roleNames = (role) =>
	cond([
		[equals('SELLER'), always(getLabelMessage('common:seller'))],
		[equals('GUARANTOR'), always(getLabelMessage('common:guarantor'))],
		[equals('REGION_MANAGER'), always(getLabelMessage('common:regionalManager'))],
		[equals('DISTRICT_MANAGER'), always(getLabelMessage('common:districtManager'))],
		[equals('AGENCY_MANAGER'), always(getLabelMessage('common:agency'))],
		[equals('GROUP_MANAGER'), always(getLabelMessage('common:manager'))],
		[equals('PPZ'), always(getLabelMessage('common:ppz'))],
		[equals('CENTRAL'), always(getLabelMessage('common:central'))],
	])(role);

const homeUrl = '/';

export const RoleSwitch = () => {
	const [opened, setIsOpened] = useState(false);

	const roles = useSelector(getRoles);
	const currentRole = useSelector(getCurrentRole);

	const buttonRef = useRef();

	const handleOpen = useCallback((event) => {
		setIsOpened(event.currentTarget);
	}, []);

	const handleClose = useCallback(() => {
		setIsOpened(null);
	}, []);

	const handleChangeRoleRedirect = useCallback(
		(role) => async () => {
			const url = getRestUrl(homeUrl, { urlParams: { role } });
			window.location.href = url;
		},
		[],
	);

	if (isEmpty(roles) || isEmpty(currentRole)) return null;

	return (
		<AccessControl accessLevel={TEMP_CAMPAIGN_DISABLE}>
			<ButtonBase onClick={handleOpen} ref={buttonRef}>
				<Stack direction="row" sx={{ alignItems: 'center', my: 2 }}>
					<Typography sx={{ mx: 1 }} variant="body2">
						{roleNames(currentRole)}
					</Typography>
					<Box sx={{ fontSize: 11, mr: 1 }}>
						<AngleDown />
					</Box>
				</Stack>
			</ButtonBase>
			<MenuPopover open={Boolean(opened)} onClose={handleClose} anchorEl={buttonRef.current}>
				<Stack>
					<Typography sx={{ color: 'text.primary', opacity: 0.5, paddingX: 2, pb: 1 }} variant="body2">
						{getLabelMessage('site:loggedAsRole')}
					</Typography>
					<Divider sx={{ borderStyle: 'dashed' }} />
					{roles &&
						roles.map((role) => (
							<MenuItem key={role} onClick={handleChangeRoleRedirect(role)}>
								<Typography variant="body2">{roleNames(role)}</Typography>
							</MenuItem>
						))}
				</Stack>
			</MenuPopover>
		</AccessControl>
	);
};

export default RoleSwitch;
